import { activeCardholderTerm } from "../utils/terminology.js";
/**
 * Partner-admin section catalog — the pure-data module → admin-section
 * map shared by cos-partner-admin-page (which composes tag/load impls
 * on top) and cos-app-shell (which renders the Option A module flyouts
 * in the Partner workspace nav from it).
 *
 * One source of truth: adding a partner-admin section means adding a
 * row HERE plus its impl entry in cos-partner-admin-page's SECTION_IMPL
 * — a parity test locks the two together. Keys are the ?s= deep-link
 * values on /partner-admin; never rename a shipped key (bookmarks).
 */

/**
 * `library` is NOT a purchased product — it is the product-neutral
 * settings group (branches, opening hours, branch groups, pickup
 * locations). Attend, Reserve, Explore, Connect and Circulate all read
 * these rows, so the group renders for every partner rather than being
 * gated on an entitlement (docs/branches-port-plan.md, D1).
 */
export type PartnerProduct =
	| "library"
	| "circulate"
	| "lend"
	| "preserve"
	| "roster"
	| "measure"
	| "thrive"
	| "nurture"
	| "broadcast"
	| "cid"
	| "attend"
	| "reserve"
	| "outreach"
	| "schedule";

export interface PartnerAdminSectionInfo {
	key: string;
	label: string;
}

export interface PartnerAdminGroupInfo {
	label: string;
	product: PartnerProduct;
	sections: PartnerAdminSectionInfo[];
}

export const PARTNER_ADMIN_GROUPS: PartnerAdminGroupInfo[] = [
	{
		label: "Library",
		product: "library",
		sections: [
			{ key: "library-branches", label: "Branches" },
			{ key: "library-hours", label: "Opening hours" },
			{ key: "library-holidays", label: "Holidays & closures" },
			{ key: "library-rooms", label: "Rooms" },
			{ key: "library-equipment-names", label: "Equipment names" },
			{ key: "library-branch-groups", label: "Branch groups" },
			{ key: "library-pickup-locations", label: "Pickup locations" },
			{ key: "library-booking-classes", label: "Booking classes" },
			{ key: "library-terminology", label: "Cardholder terminology" },
			{ key: "library-patron-data", get label() { return `${activeCardholderTerm().Singular} data`; } },
			{ key: "library-webhooks", label: "Webhooks" },
			{ key: "library-ai", label: "AI features" },
			{ key: "library-sending-hours", label: "Sending hours" },
		],
	},
	{
		label: "Circulate",
		product: "circulate",
		sections: [
			{ key: "circ-rules", label: "Circulation rules" },
			{ key: "patron-categories", get label() { return `${activeCardholderTerm().Singular} categories`; } },
			{ key: "item-types", label: "Item types" },
			{ key: "system-settings", label: "System settings" },
			{ key: "auth-values", label: "Authorized values" },
			// No "Holiday calendars" here on purpose: closures are the
			// Library group's "Holidays & closures" (key library-holidays),
			// one place for every module. Circulate's own parallel screen and
			// its Postgres tables were retired 2026-07-30 — it reads hours and
			// closures from COS now, via GET /internal/library/hours-status.
			{ key: "item-alerts", label: "Item alerts" },
			{ key: "age-rules", label: "Item age rules" },
			{ key: "audio-alerts", label: "Audio alerts" },
			{ key: "library-groups", label: "Library groups" },
			{ key: "cities", label: "Cities" },
			{ key: "record-sources", label: "Record sources" },
			{ key: "marc-mod-templates", label: "MARC mod templates" },
			{ key: "csv-profiles", label: "CSV profiles" },
			{ key: "news", label: "News" },
			{ key: "tags", label: "Tag moderation" },
			{ key: "audit-log", label: "Audit log" },
			{ key: "permissions", label: "Permissions" },
			// Wave 3b (docs/modules/branches-and-hours.md): which Communico
			// branch each Circulate org unit maps to. Lives here, not under
			// "Lend", because the mapping itself is a Circulate concept (org
			// units don't exist without Circulate) even though Lend's item
			// twins are what it unblocks.
			{ key: "circ-branch-mapping", label: "Branch mapping" },
			// Track 4 F3 (docs/modules/circulate.md): self-check (SIP2) machines and
			// the library's address allow list for them, and the optional
			// network limit on staff access through the COS proxy. Keys are
			// deep links: never rename them.
			{ key: "sip2-terminals", label: "Self-check machines" },
			{ key: "circ-api-access", label: "Staff access by network" },
		],
	},
	{
		label: "Library of Things",
		product: "lend",
		sections: [
			{ key: "lend-categories", label: "Categories & lending rules" },
			{ key: "lend-float-groups", label: "Float groups" },
			{ key: "lend-waivers", label: "Waivers" },
			{ key: "lend-program", label: "Program" },
		],
	},
	{
		label: "Preserve",
		product: "preserve",
		sections: [
			{ key: "preserve-collections", label: "Collections" },
			{ key: "preserve-engagement", label: "Community contributions" },
			{ key: "preserve-cataloguing", label: "Cataloguing setup" },
			{ key: "preserve-templates", label: "Metadata templates" },
			{ key: "preserve-field-display", label: "Display settings" },
			{ key: "preserve-updates", label: "Updates" },
			{ key: "preserve-exhibits", label: "Exhibits" },
			{ key: "preserve-donors", label: "Donors" },
			{ key: "preserve-branches", label: "Branches" },
			{ key: "preserve-import", label: "CSV import" },
			{ key: "preserve-transcription", label: "Transcription" },
			{ key: "preserve-vocabulary", label: "Vocabulary" },
			{ key: "preserve-usage", label: "Usage" },
		],
	},
	{
		label: "Community Info",
		product: "cid",
		sections: [
			{ key: "cid-agencies", label: "Agencies" },
			{ key: "cid-taxonomy", label: "Browse categories" },
			{ key: "cid-emergency", label: "Community Emergency" },
		],
	},
	{
		label: "Roster",
		product: "roster",
		sections: [
			{ key: "roster-branches", label: "Branches" },
			{ key: "roster-service-points", label: "Service points" },
			{ key: "roster-rotations", label: "Rotations" },
			{ key: "roster-sub-pools", label: "Substitute pools" },
			{ key: "roster-clock-tags", label: "Clock-in points" },
			{ key: "roster-skills", label: "Skills & leave types" },
			{ key: "roster-coverage", label: "Coverage rules" },
			{ key: "roster-commitment-tiers", label: "Commitment-driven tiers" },
			{ key: "roster-staffing", label: "Staffing priorities" },
			{ key: "roster-roles", label: "Operational roles" },
			{ key: "roster-rules", label: "Compliance rules" },
			{ key: "roster-enforcement", label: "Enforcement policy" },
			{ key: "roster-access", label: "Access & review" },
			{ key: "roster-breaks", label: "Break policy" },
			{ key: "roster-pay-periods", label: "Pay periods" },
			{ key: "roster-commitment-cutoff", label: "Commitment cutoff" },
			{ key: "roster-notices", label: "Notice templates" },
			{ key: "roster-invoicing", label: "Presenter invoicing" },
			{ key: "roster-costs", label: "Costs & visibility" },
			{ key: "roster-implementation-mode", label: "Implementation status" },
			{ key: "roster-departments", label: "Departments" },
			{ key: "roster-hours-figures", label: "Hours figures" },
			{ key: "roster-secondary-coverage", label: "Second responsibilities" },
			{ key: "roster-claim-eligibility", label: "Claim eligibility" },
			{ key: "roster-task-tags", label: "Task codes" },
			{ key: "roster-offdesk-targets", label: "Off-desk targets" },
			{ key: "roster-payroll", label: "Payroll export" },
			{ key: "roster-schedule-import", label: "Schedule import codes" },
		],
	},
	{
		label: "Attend",
		product: "attend",
		sections: [
			{ key: "attend-types", label: "Event types" },
			{ key: "attend-ages", label: "Age groups" },
			{ key: "attend-tags", label: "Search tags" },
			{ key: "attend-reporting", label: "Reporting categories" },
			{ key: "attend-mediation", label: "Mediation groups" },
			{ key: "attend-notices", label: "Notice templates" },
			{ key: "attend-settings", label: "Event settings" },
			{ key: "attend-checkin", label: "Check-in" },
		],
	},
	{
		label: "Reserve",
		product: "reserve",
		sections: [
			{ key: "reserve-panels", label: "Devices" },
			{ key: "reserve-notices", label: "Notice templates" },
		],
	},
	{
		// fleet/thrive-w2-ui — Thrive replaces Measure in the rail (Paul,
		// 2026-09-06); this admin section follows, keyed on "thrive" alone:
		// Thrive is sold separately from Measure (Paul, 1 November 2026).
		label: "Thrive",
		product: "thrive",
		sections: [{ key: "measure-community-reach", label: "Community Reach" }],
	},
	{
		get label() { return `${activeCardholderTerm().Singular} CRM`; },
		product: "nurture",
		sections: [
			{ key: "nurture-permissions", label: "Permissions & visibility" },
			{ key: "nurture-bucket-thresholds", label: "Bucket thresholds" },
			{ key: "nurture-card-conversion", label: "Card conversion" },
			{ key: "nurture-brand-kit", label: "Brand kit" },
			{ key: "nurture-brand-presets", label: "Header & footer presets" },
			{ key: "nurture-email-tracking", label: "Email open tracking" },
			{ key: "nurture-contact-types", label: "Contact types" },
			{ key: "nurture-tags", label: "Tags" },
			{ key: "nurture-org-types", label: "Organisation types" },
			{ key: "nurture-roles", label: "Roles" },
			{ key: "nurture-custom-fields", label: "Contact fields" },
			{ key: "nurture-email-inboxes", label: "Shared email" },
		],
	},
];

/**
 * Products that are NOT entitlements — their admin group renders for
 * every partner. Today that is only the product-neutral Library group.
 */
export const UNGATED_ADMIN_PRODUCTS: ReadonlySet<PartnerProduct> = new Set<PartnerProduct>(["library"]);

/**
 * Products whose admin group a library's own admin (level 3) opens on
 * /partner-admin, for their own library only. Paul, 31 October 2026:
 * library admins set Roster up themselves; every other group stays
 * Communico admin only. The page shows a library admin nothing else, and
 * the server holds the same line (each Roster setup route is level 3 and
 * scoped to the caller's library).
 * Paul, 1 November 2026: Preserve (Digital Archives) joins Roster. All of its
 * setup sections open to a library admin for their own library; the two
 * Communico-only Preserve pages (limits, Internet Archive import) are not
 * sections here, they are /admin routes, so nothing on this list leads to them.
 * Paul, 2 November 2026: Lend (Library of Things) joins them. All four of its
 * sections (categories and lending rules, float groups, waivers, program
 * settings) open to a library admin for their own library; the server holds
 * the same line. It stays off the linked level 2 set below.
 */
export const LIBRARY_ADMIN_PRODUCTS: ReadonlySet<PartnerProduct> = new Set<PartnerProduct>(["roster", "preserve", "lend"]);

/** What a linked level 2 login opens on /partner-admin: Preserve only, because
 *  Preserve's routes pin a level 2 to its own library and Roster's do not. */
export const LIBRARY_LEVEL2_PRODUCTS: ReadonlySet<PartnerProduct> = new Set<PartnerProduct>(["preserve"]);

/** The admin group for a product, or undefined (broadcast has none yet). */
export function adminGroupForProduct(product: string): PartnerAdminGroupInfo | undefined {
	return PARTNER_ADMIN_GROUPS.find((g) => g.product === product);
}

/** Whether an account's entitled product list satisfies `product`. A plain
 *  membership test: Thrive is sold on its own (Paul, 1 November 2026), so the
 *  Thrive admin section is shown to a library that bought Thrive and never
 *  because it holds Measure. */
export function productEntitled(products: string[], product: PartnerProduct): boolean {
	return products.includes(product);
}

/**
 * Plain-language module names for the Partner workspace (Paul, 2026-07-17):
 * partners see the plain-English description as the MAIN nav label / page
 * title, with the Communico module brand as the subtitle — covering both
 * bases with plain English leading. ONE source of truth for the rail rows,
 * the Admin module-picker flyout, and the /partner-admin page title.
 * `sub` = the module's product/brand name; `main` = the plain function.
 * Attend + Broadcast are Paul's naming; the newer COS-native modules were
 * created to the same pattern (adjust freely — they're just labels).
 */
export const PARTNER_MODULE_LABELS: Record<PartnerProduct, { main: string; sub: string }> = {
	// The Library group has no brand to carry — it is settings every
	// partner has, not a module anyone bought.
	library: { main: "Library Settings", sub: "Branches & hours" },
	circulate: { main: "Library Management System", sub: "Circulate" },
	lend: { main: "Library of Things", sub: "Lend" },
	preserve: { main: "Digital Archives", sub: "Preserve" },
	roster: { main: "Staff Scheduling", sub: "Roster" },
	measure: { main: "Analytics & Reporting", sub: "Measure" },
	// Thrive replaces Measure in the rail (Paul, 2026-09-06); it has no
	// separate Communico brand name distinct from itself yet (the Outreach
	// precedent above), so main and sub match.
	thrive: { main: "Thrive", sub: "Thrive" },
	nurture: { main: "Contacts and Marketing", sub: "Nurture" },
	broadcast: { main: "Digital Signage", sub: "Broadcast" },
	attend: { main: "Events & Program Management", sub: "Attend" },
	cid: { main: "Community Directory", sub: "Community Info" },
	reserve: { main: "Room & Space Booking", sub: "Reserve" },
	// Outreach has no separate Communico brand name distinct from its
	// plain-English function: unlike Circulate/Reserve/Attend it was
	// never sold under a different marketing name, so main and sub match
	// (docs/outreach-spec.md's amended §1.3 note).
	outreach: { main: "Outreach", sub: "Outreach" },
	// The rail says "Appointments": COS already has Roster for staff
	// schedules, so the product name Schedule stays in code and routes.
	schedule: { main: "Appointments", sub: "Schedule" },
};
